/**
 * mdlondon Performance Optimizer
 * Handles lazy loading, image optimization, and performance monitoring
 */

class PerformanceOptimizer {
  constructor() {
    this.init();
  }

  init() {
    this.setupLazyLoading();
    this.setupImageOptimization();
    this.setupPerformanceMonitoring();
    this.setupAnalytics();
  }

  // Lazy Loading
  setupLazyLoading() {
    if ('IntersectionObserver' in window) {
      this.observeLazyImages();
      this.observeLazySections();
    } else {
      // Fallback: load all images immediately
      this.loadAllImages();
    }
  }

  observeLazyImages() {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          this.loadImage(img);
          observer.unobserve(img);
        }
      });
    }, {
      rootMargin: '50px 0px',
      threshold: 0.01
    });

    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      imageObserver.observe(img);
    });
  }

  observeLazySections() {
    const sectionObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const section = entry.target;
          section.classList.add('section--loaded');
          observer.unobserve(section);
        }
      });
    }, {
      rootMargin: '100px 0px'
    });

    document.querySelectorAll('[data-lazy-section]').forEach(section => {
      sectionObserver.observe(section);
    });
  }

  loadImage(img) {
    if (img.dataset.src) {
      img.src = img.dataset.src;
    }
    if (img.dataset.srcset) {
      img.srcset = img.dataset.srcset;
    }
    img.classList.add('loaded');
  }

  loadAllImages() {
    document.querySelectorAll('img[loading="lazy"]').forEach(img => {
      this.loadImage(img);
    });
  }

  // Image Optimization
  setupImageOptimization() {
    // Convert images to WebP if supported
    if (this.supportsWebP()) {
      document.documentElement.classList.add('webp-supported');
    }

    // Responsive images
    this.optimizeResponsiveImages();
  }

  supportsWebP() {
    const elem = document.createElement('canvas');
    if (elem.getContext && elem.getContext('2d')) {
      return elem.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    }
    return false;
  }

  optimizeResponsiveImages() {
    const images = document.querySelectorAll('img[data-sizes="auto"]');
    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          const img = entry.target;
          const width = entry.contentRect.width;
          this.updateImageSize(img, width);
        });
      });

      images.forEach(img => resizeObserver.observe(img));
    }
  }

  updateImageSize(img, width) {
    // Round up to nearest breakpoint
    const sizes = [400, 600, 800, 1000, 1200, 1600, 2000];
    const targetSize = sizes.find(size => size >= width) || sizes[sizes.length - 1];

    if (img.dataset.baseUrl) {
      const newSrc = img.dataset.baseUrl.replace('{width}', targetSize);
      if (img.src !== newSrc) {
        img.src = newSrc;
      }
    }
  }

  // Performance Monitoring
  setupPerformanceMonitoring() {
    if ('PerformanceObserver' in window) {
      this.monitorLCP();
      this.monitorFID();
      this.monitorCLS();
    }

    // Page load metrics
    window.addEventListener('load', () => {
      this.logPageLoadMetrics();
    });
  }

  monitorLCP() {
    // Largest Contentful Paint
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      this.logMetric('LCP', lastEntry.renderTime || lastEntry.loadTime);
    });

    try {
      lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    } catch (e) {
      console.warn('LCP monitoring not supported');
    }
  }

  monitorFID() {
    // First Input Delay
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach(entry => {
        this.logMetric('FID', entry.processingStart - entry.startTime);
      });
    });

    try {
      fidObserver.observe({ entryTypes: ['first-input'] });
    } catch (e) {
      console.warn('FID monitoring not supported');
    }
  }

  monitorCLS() {
    // Cumulative Layout Shift
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
          this.logMetric('CLS', clsValue);
        }
      }
    });

    try {
      clsObserver.observe({ entryTypes: ['layout-shift'] });
    } catch (e) {
      console.warn('CLS monitoring not supported');
    }
  }

  logPageLoadMetrics() {
    if (window.performance && window.performance.timing) {
      const timing = window.performance.timing;
      const metrics = {
        dns: timing.domainLookupEnd - timing.domainLookupStart,
        tcp: timing.connectEnd - timing.connectStart,
        ttfb: timing.responseStart - timing.requestStart,
        download: timing.responseEnd - timing.responseStart,
        domInteractive: timing.domInteractive - timing.navigationStart,
        domComplete: timing.domComplete - timing.navigationStart,
        loadComplete: timing.loadEventEnd - timing.navigationStart
      };

      console.log('Page Load Metrics:', metrics);
      this.sendMetricsToAnalytics(metrics);
    }
  }

  logMetric(name, value) {
    console.log('Performance Metric - ' + name + ':', value);

    // Send to analytics if configured
    if (window.gtag) {
      window.gtag('event', 'web_vitals', {
        event_category: 'Performance',
        event_label: name,
        value: Math.round(value),
        non_interaction: true
      });
    }
  }

  sendMetricsToAnalytics(metrics) {
    if (window.gtag) {
      window.gtag('event', 'page_load_metrics', {
        event_category: 'Performance',
        ...metrics,
        non_interaction: true
      });
    }
  }

  // Analytics Setup
  setupAnalytics() {
    // Enhanced ecommerce tracking
    this.trackProductViews();
    this.trackAddToCart();
    this.trackCheckout();
  }

  trackProductViews() {
    const productElement = document.querySelector('[data-product-id]');
    if (!productElement || !window.gtag) return;

    const productId = productElement.dataset.productId;
    const productTitle = document.querySelector('.product-title')?.textContent;
    const productPrice = document.querySelector('.product-price')?.textContent;

    if (productId && productTitle) {
      window.gtag('event', 'view_item', {
        items: [{
          item_id: productId,
          item_name: productTitle,
          price: productPrice
        }]
      });
    }
  }

  trackAddToCart() {
    document.addEventListener('cart:item-added', (e) => {
      if (!window.gtag) return;

      window.gtag('event', 'add_to_cart', {
        items: [{
          item_id: e.detail.variantId,
          quantity: e.detail.quantity
        }]
      });
    });
  }

  trackCheckout() {
    if (window.location.pathname.includes('/checkout')) {
      if (window.gtag) {
        window.gtag('event', 'begin_checkout');
      }
    }
  }
}

// Initialize
new PerformanceOptimizer();
